import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import config from '@config'
import { cn } from '@/lib/cn'

interface NodeState {
  position: THREE.Vector3
  velocity: THREE.Vector3
  home: THREE.Vector3
}

interface GlowSpriteState {
  sprite: THREE.Sprite
  nodeIdx: number
}

interface NeuralCanvasProps {
  speedMultiplier?: number
  motionMultiplier?: number
  className?: string
}

export function NeuralCanvas({
  speedMultiplier = 1,
  motionMultiplier = 1,
  className,
}: NeuralCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mouseRef = useRef(new THREE.Vector2(0, 0))

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const {
      nodeCount,
      connectionDistance,
      particleColor,
      lineColor,
      mouseInfluenceRadius,
      mouseInfluenceStrength,
      meshWidth,
      meshHeight,
      meshSegmentsX,
      meshSegmentsY,
      meshAmplitude,
      meshFrequency,
      meshSpeed,
      meshOpacity,
      meshColor,
    } = config.animation
    const speedFactor = Math.max(0.25, speedMultiplier)
    const motionFactor = Math.max(0.35, motionMultiplier)

    // ── Renderer ──────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(container.clientWidth, container.clientHeight)
    renderer.setClearColor(0x000000, 0)
    container.appendChild(renderer.domElement)

    // ── Scene & camera ────────────────────────────────────────────────────────
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      0.1,
      1000,
    )
    camera.position.set(0, 0, 380)
    scene.fog = new THREE.FogExp2(new THREE.Color(meshColor), 0.0014)

    // ── Animated network graph backdrop ─────────────────────────────────────
    const graphCols = Math.max(28, Math.floor(meshSegmentsX * 1.05))
    const graphRows = Math.max(16, Math.floor(meshSegmentsY * 1.05))
    const graphNodeCount = graphCols * graphRows
    const graphDepth = -130
    const graphDistance = camera.position.z - graphDepth
    const verticalFovRad = THREE.MathUtils.degToRad(camera.fov)
    const viewHeightAtGraphDepth = 2 * Math.tan(verticalFovRad / 2) * graphDistance
    const viewWidthAtGraphDepth = viewHeightAtGraphDepth * camera.aspect
    const graphWidth = Math.max(meshWidth * 1.22, viewWidthAtGraphDepth * 1.25)
    const graphHeight = Math.max(meshHeight * 1.22, viewHeightAtGraphDepth * 1.25)

    const graphBasePosArray = new Float32Array(graphNodeCount * 3)
    const graphPosArray = new Float32Array(graphNodeCount * 3)
    const graphPhaseArray = new Float32Array(graphNodeCount)

    for (let row = 0; row < graphRows; row++) {
      for (let col = 0; col < graphCols; col++) {
        const idx = row * graphCols + col
        const base = idx * 3
        const x = (col / (graphCols - 1) - 0.5) * graphWidth + (Math.random() - 0.5) * 2.4
        const y = (row / (graphRows - 1) - 0.5) * graphHeight + (Math.random() - 0.5) * 2.4
        const z = graphDepth + (Math.random() - 0.5) * 7

        graphBasePosArray[base] = x
        graphBasePosArray[base + 1] = y
        graphBasePosArray[base + 2] = z
        graphPosArray[base] = x
        graphPosArray[base + 1] = y
        graphPosArray[base + 2] = z
        graphPhaseArray[idx] = Math.random() * Math.PI * 2
      }
    }

    const graphEdges: Array<[number, number]> = []
    for (let row = 0; row < graphRows; row++) {
      for (let col = 0; col < graphCols; col++) {
        const idx = row * graphCols + col
        if (col < graphCols - 1) {
          graphEdges.push([idx, idx + 1])
        }
        if (row < graphRows - 1) {
          graphEdges.push([idx, idx + graphCols])
        }
      }
    }
    for (let row = 0; row < graphRows - 1; row++) {
      for (let col = 0; col < graphCols - 1; col++) {
        const idx = row * graphCols + col
        if ((row + col) % 2 === 0) {
          graphEdges.push([idx, idx + graphCols + 1])
        } else {
          graphEdges.push([idx + 1, idx + graphCols])
        }
      }
    }

    const graphLineArray = new Float32Array(graphEdges.length * 6)
    const graphLineGeo = new THREE.BufferGeometry()
    const graphLineAttr = new THREE.BufferAttribute(graphLineArray, 3)
    graphLineAttr.setUsage(THREE.DynamicDrawUsage)
    graphLineGeo.setAttribute('position', graphLineAttr)

    const graphLineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(meshColor),
      transparent: true,
      opacity: Math.min(0.32, meshOpacity + 0.08),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const graphLines = new THREE.LineSegments(graphLineGeo, graphLineMat)
    scene.add(graphLines)

    const graphPointsGeo = new THREE.BufferGeometry()
    const graphPointsAttr = new THREE.BufferAttribute(graphPosArray, 3)
    graphPointsAttr.setUsage(THREE.DynamicDrawUsage)
    graphPointsGeo.setAttribute('position', graphPointsAttr)

    const graphPointsMat = new THREE.PointsMaterial({
      color: new THREE.Color(meshColor),
      size: 1.6,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.44,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const graphPoints = new THREE.Points(graphPointsGeo, graphPointsMat)
    scene.add(graphPoints)

    const graphFrameGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-graphWidth / 2, -graphHeight / 2, graphDepth),
      new THREE.Vector3(graphWidth / 2, -graphHeight / 2, graphDepth),
      new THREE.Vector3(graphWidth / 2, graphHeight / 2, graphDepth),
      new THREE.Vector3(-graphWidth / 2, graphHeight / 2, graphDepth),
    ])
    const graphFrameMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(meshColor),
      transparent: true,
      opacity: 0.14,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const graphFrame = new THREE.LineLoop(graphFrameGeo, graphFrameMat)
    scene.add(graphFrame)

    const updateGraphLines = () => {
      for (let edgeIdx = 0; edgeIdx < graphEdges.length; edgeIdx++) {
        const [startIdx, endIdx] = graphEdges[edgeIdx]
        const startBase = startIdx * 3
        const endBase = endIdx * 3
        const lineBase = edgeIdx * 6

        graphLineArray[lineBase] = graphPosArray[startBase]
        graphLineArray[lineBase + 1] = graphPosArray[startBase + 1]
        graphLineArray[lineBase + 2] = graphPosArray[startBase + 2]
        graphLineArray[lineBase + 3] = graphPosArray[endBase]
        graphLineArray[lineBase + 4] = graphPosArray[endBase + 1]
        graphLineArray[lineBase + 5] = graphPosArray[endBase + 2]
      }
    }
    updateGraphLines()

    const graphIndexForPosition = (x: number, y: number) => {
      const u = THREE.MathUtils.clamp(x / graphWidth + 0.5, 0, 1)
      const v = THREE.MathUtils.clamp(y / graphHeight + 0.5, 0, 1)
      const col = Math.max(0, Math.min(graphCols - 1, Math.round(u * (graphCols - 1))))
      const row = Math.max(0, Math.min(graphRows - 1, Math.round(v * (graphRows - 1))))
      return row * graphCols + col
    }

    // ── Nodes ─────────────────────────────────────────────────────────────────
    const nodes: NodeState[] = []
    const W = 320, H = 200

    for (let i = 0; i < nodeCount; i++) {
      const pos = new THREE.Vector3(
        (Math.random() - 0.5) * W * 2,
        (Math.random() - 0.5) * H * 2,
        (Math.random() - 0.5) * 60,
      )
      nodes.push({
        position: pos.clone(),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.25,
          (Math.random() - 0.5) * 0.25,
          0,
        ),
        home: pos.clone(),
      })
    }

    // ── Point sprites ─────────────────────────────────────────────────────────
    const posArray = new Float32Array(nodeCount * 3)
    nodes.forEach((n, i) => {
      posArray[i * 3]     = n.position.x
      posArray[i * 3 + 1] = n.position.y
      posArray[i * 3 + 2] = n.position.z
    })
    const pointsGeo = new THREE.BufferGeometry()
    const posAttr = new THREE.BufferAttribute(posArray, 3)
    posAttr.setUsage(THREE.DynamicDrawUsage)
    pointsGeo.setAttribute('position', posAttr)

    const pointsMat = new THREE.PointsMaterial({
      color: new THREE.Color(particleColor),
      size: 3.5,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.9,
    })
    const pointsMesh = new THREE.Points(pointsGeo, pointsMat)
    scene.add(pointsMesh)

    // ── Lines ─────────────────────────────────────────────────────────────────
    const maxSegments = nodeCount * 12
    const lineArr = new Float32Array(maxSegments * 6)
    const lineGeo = new THREE.BufferGeometry()
    const lineAttr = new THREE.BufferAttribute(lineArr, 3)
    lineAttr.setUsage(THREE.DynamicDrawUsage)
    lineGeo.setAttribute('position', lineAttr)

    const lineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(lineColor),
      transparent: true,
      opacity: 0.18,
    })
    const linesMesh = new THREE.LineSegments(lineGeo, lineMat)
    scene.add(linesMesh)

    // ── Bridge lines: connect dynamic nodes to mesh graph ────────────────────
    const bridgeCount = Math.max(4, Math.min(12, Math.floor(nodeCount * 0.12)))
    const bridgeNodeIndices = Array.from({ length: bridgeCount }, (_, i) =>
      Math.floor((i / bridgeCount) * nodeCount),
    )
    const bridgeLineArray = new Float32Array(bridgeCount * 6)
    const bridgeLineGeo = new THREE.BufferGeometry()
    const bridgeLineAttr = new THREE.BufferAttribute(bridgeLineArray, 3)
    bridgeLineAttr.setUsage(THREE.DynamicDrawUsage)
    bridgeLineGeo.setAttribute('position', bridgeLineAttr)

    const bridgeLineMat = new THREE.LineBasicMaterial({
      color: new THREE.Color(meshColor),
      transparent: true,
      opacity: 0.14,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    scene.add(new THREE.LineSegments(bridgeLineGeo, bridgeLineMat))
    const bridgeMappedX = new Float32Array(bridgeCount)
    const bridgeMappedY = new Float32Array(bridgeCount)
    const bridgeEnergy = new Float32Array(bridgeCount)
    const bridgeInfluenceRadius = Math.max(86, Math.min(graphWidth, graphHeight) * 0.16)
    const bridgeInfluenceInvSq = 1 / (bridgeInfluenceRadius * bridgeInfluenceRadius)

    // ── Glow sprites for highlighted nodes ────────────────────────────────────
    // Create a small circle texture for glowing nodes
    const glowCanvas = document.createElement('canvas')
    glowCanvas.width = 64
    glowCanvas.height = 64
    const ctx = glowCanvas.getContext('2d')!
    const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
    grad.addColorStop(0, 'rgba(167,139,250,0.8)')
    grad.addColorStop(0.3, 'rgba(124,58,237,0.4)')
    grad.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, 64, 64)
    const glowTexture = new THREE.CanvasTexture(glowCanvas)

    const glowMat = new THREE.SpriteMaterial({
      map: glowTexture,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })

    // Add glow sprites to a few random nodes
    const glowSprites: GlowSpriteState[] = []
    for (let i = 0; i < Math.min(15, nodeCount); i++) {
      const idx = Math.floor(Math.random() * nodeCount)
      const sprite = new THREE.Sprite(glowMat)
      sprite.scale.set(18, 18, 1)
      sprite.position.copy(nodes[idx].position)
      scene.add(sprite)
      glowSprites.push({ sprite, nodeIdx: idx })
    }

    // ── Ambient stars for depth ───────────────────────────────────────────────
    const starCount = Math.max(220, Math.floor(nodeCount * 5))
    const starPosArray = new Float32Array(starCount * 3)
    for (let i = 0; i < starCount; i++) {
      const base = i * 3
      starPosArray[base] = (Math.random() - 0.5) * graphWidth * 2.2
      starPosArray[base + 1] = (Math.random() - 0.5) * graphHeight * 2.2
      starPosArray[base + 2] = -260 - Math.random() * 340
    }

    const starsGeo = new THREE.BufferGeometry()
    starsGeo.setAttribute('position', new THREE.BufferAttribute(starPosArray, 3))
    const starsMat = new THREE.PointsMaterial({
      color: new THREE.Color(meshColor),
      size: 1.3,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.26,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
    const starsMesh = new THREE.Points(starsGeo, starsMat)
    scene.add(starsMesh)

    // ── Pointer-reactive energy rings ─────────────────────────────────────────
    const ringGroup = new THREE.Group()
    ringGroup.position.set(0, 0, 70)
    scene.add(ringGroup)

    const ringMeshes: THREE.Mesh[] = []
    for (let i = 0; i < 3; i++) {
      const ringGeo = new THREE.RingGeometry(24 + i * 30, 26 + i * 30, 96)
      const ringMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(meshColor),
        transparent: true,
        opacity: 0.04,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
      const ringMesh = new THREE.Mesh(ringGeo, ringMat)
      ringMesh.rotation.x = Math.PI / 2.35
      ringMesh.rotation.z = i * 0.48
      ringMesh.position.z = 34 - i * 7
      ringGroup.add(ringMesh)
      ringMeshes.push(ringMesh)
    }

    // ── Mouse tracking ────────────────────────────────────────────────────────
    let pointerInside = false
    let pointerEnergy = 0
    let pointerEnergyTarget = 0

    function onPointerMove(e: PointerEvent) {
      const rect = container!.getBoundingClientRect()
      mouseRef.current.set(
        ((e.clientX - rect.left) / rect.width)  * 2 - 1,
        -((e.clientY - rect.top)  / rect.height) * 2 + 1,
      )
      pointerInside = true
      pointerEnergyTarget = 1
    }

    function onPointerLeave() {
      pointerInside = false
      pointerEnergyTarget = 0
    }

    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerdown', onPointerMove)
    container.addEventListener('pointerleave', onPointerLeave)

    // ── Resize ────────────────────────────────────────────────────────────────
    function onResize() {
      if (!container) return
      camera.aspect = container.clientWidth / container.clientHeight
      camera.updateProjectionMatrix()
      renderer.setSize(container.clientWidth, container.clientHeight)
    }
    window.addEventListener('resize', onResize)

    // ── Animation ─────────────────────────────────────────────────────────────
    let animId: number
    const clock = new THREE.Clock()
    const mouseWorld = new THREE.Vector3()
    const tmpDir = new THREE.Vector3()
    const origin = new THREE.Vector3(0, 0, 0)

    function animate() {
      animId = requestAnimationFrame(animate)
      const elapsed = clock.getElapsedTime()

      const targetCameraX = (pointerInside ? mouseRef.current.x : 0) * 18
      const targetCameraY = (pointerInside ? mouseRef.current.y : 0) * 12
      camera.position.x += (targetCameraX - camera.position.x) * 0.045
      camera.position.y += ((-targetCameraY) - camera.position.y) * 0.045
      camera.position.z = 380 + Math.sin(elapsed * (0.18 * speedFactor)) * (4 * motionFactor)
      camera.lookAt(0, 0, 0)

      pointerEnergyTarget *= pointerInside ? 0.992 : 0.94
      pointerEnergy += (pointerEnergyTarget - pointerEnergy) * 0.08

      let networkBridgeEnergy = 0
      for (let bridgeIdx = 0; bridgeIdx < bridgeCount; bridgeIdx++) {
        const nodeIdx = bridgeNodeIndices[bridgeIdx]
        const node = nodes[nodeIdx]
        bridgeMappedX[bridgeIdx] = (node.position.x / (W * 2)) * graphWidth
        bridgeMappedY[bridgeIdx] = (node.position.y / (H * 2)) * graphHeight
        bridgeEnergy[bridgeIdx] = Math.min(1.2, node.velocity.length() * 8 + pointerEnergy * 0.6)
        networkBridgeEnergy += bridgeEnergy[bridgeIdx]
      }
      const normalizedBridgeEnergy = Math.min(1.1, networkBridgeEnergy / bridgeCount)
      const globalDriftX = Math.sin(elapsed * (meshSpeed * 0.16 * speedFactor)) * graphWidth * (0.01 * motionFactor)
      const globalDriftY = Math.cos(elapsed * (meshSpeed * 0.13 * speedFactor)) * graphHeight * (0.008 * motionFactor)
      const graphLocalDrift = Math.max(2.8, meshAmplitude * 0.34 * motionFactor)

      // Animate graph mesh with drift + ripple from network nodes.
      for (let i = 0; i < graphPosArray.length; i += 3) {
        const nodeIdx = i / 3
        const baseX = graphBasePosArray[i]
        const baseY = graphBasePosArray[i + 1]
        const baseZ = graphBasePosArray[i + 2]
        const phase = graphPhaseArray[nodeIdx]
        let bridgeInfluence = 0

        for (let bridgeIdx = 0; bridgeIdx < bridgeCount; bridgeIdx++) {
          const dx = baseX - bridgeMappedX[bridgeIdx]
          const dy = baseY - bridgeMappedY[bridgeIdx]
          const distSq = dx * dx + dy * dy
          const falloff = 1 / (1 + distSq * bridgeInfluenceInvSq)
          bridgeInfluence += falloff * bridgeEnergy[bridgeIdx]
        }
        const interaction = Math.min(1.8, bridgeInfluence)

        graphPosArray[i] = baseX
          + globalDriftX
          + Math.sin(baseY * (meshFrequency * 1.7) + elapsed * (meshSpeed * 0.22 * speedFactor) + phase) * graphLocalDrift
          + Math.sin(elapsed * (meshSpeed * 0.62 * speedFactor) + phase * 1.1) * interaction * 0.62 * motionFactor
        graphPosArray[i + 1] = baseY
          + globalDriftY
          + Math.cos(baseX * (meshFrequency * 1.55) - elapsed * (meshSpeed * 0.18 * speedFactor) + phase * 0.75) * graphLocalDrift
          + Math.cos(elapsed * (meshSpeed * 0.54 * speedFactor) + phase * 0.6) * interaction * 0.54 * motionFactor
        graphPosArray[i + 2] = baseZ
          + Math.sin((baseX + baseY) * (meshFrequency * 1.1) + elapsed * (meshSpeed * 0.5 * speedFactor) + phase) * (meshAmplitude * 0.22 * motionFactor)
          + interaction * (meshAmplitude * 0.36 * motionFactor)
      }
      graphPointsAttr.needsUpdate = true
      updateGraphLines()
      graphLineAttr.needsUpdate = true
      graphLineMat.opacity = Math.min(0.4, meshOpacity + 0.11 + Math.sin(elapsed * (0.55 * speedFactor)) * 0.04 + normalizedBridgeEnergy * 0.08)
      graphPointsMat.opacity = 0.4 + Math.sin(elapsed * (0.45 * speedFactor)) * 0.05 + normalizedBridgeEnergy * 0.08
      graphFrameMat.opacity = 0.11 + Math.sin(elapsed * (0.35 * speedFactor)) * 0.02 + normalizedBridgeEnergy * 0.03
      starsMat.opacity = 0.22 + Math.sin(elapsed * (0.6 * speedFactor) + 0.8) * 0.05 + pointerEnergy * 0.07
      starsMesh.rotation.z = elapsed * (0.008 * speedFactor)
      starsMesh.rotation.x = Math.sin(elapsed * (0.1 * speedFactor)) * 0.04

      if (pointerInside) {
        mouseWorld.set(mouseRef.current.x, mouseRef.current.y, 0.5).unproject(camera)
        mouseWorld.z = 0
      } else {
        mouseWorld.lerp(origin, 0.1)
      }

      for (let i = 0; i < nodeCount; i++) {
        const n = nodes[i]

        // Mouse repulsion
        if (pointerInside) {
          tmpDir.subVectors(n.position, mouseWorld)
          const dist = tmpDir.length()
          if (dist < mouseInfluenceRadius && dist > 0.01) {
            const force = (1 - dist / mouseInfluenceRadius) * mouseInfluenceStrength
            n.velocity.addScaledVector(tmpDir.normalize(), force * 1.5)
          }
        }

        // Spring toward home
        tmpDir.subVectors(n.home, n.position)
        n.velocity.addScaledVector(tmpDir, 0.003)

        // Dampen
        n.velocity.multiplyScalar(0.97)

        // Integrate
        n.position.add(n.velocity)

        // Write to buffer
        posArray[i * 3]     = n.position.x
        posArray[i * 3 + 1] = n.position.y
        posArray[i * 3 + 2] = n.position.z
      }
      pointsGeo.attributes.position.needsUpdate = true

      // Draw dynamic bridges from network nodes into the graph mesh.
      for (let bridgeIdx = 0; bridgeIdx < bridgeCount; bridgeIdx++) {
        const nodeIdx = bridgeNodeIndices[bridgeIdx]
        const node = nodes[nodeIdx]
        const mappedX = (node.position.x / (W * 2)) * graphWidth
        const mappedY = (node.position.y / (H * 2)) * graphHeight
        const graphTargetBase = graphIndexForPosition(mappedX, mappedY) * 3
        const lineBase = bridgeIdx * 6

        bridgeLineArray[lineBase] = node.position.x
        bridgeLineArray[lineBase + 1] = node.position.y
        bridgeLineArray[lineBase + 2] = node.position.z
        bridgeLineArray[lineBase + 3] = graphPosArray[graphTargetBase]
        bridgeLineArray[lineBase + 4] = graphPosArray[graphTargetBase + 1]
        bridgeLineArray[lineBase + 5] = graphPosArray[graphTargetBase + 2]
      }
      bridgeLineAttr.needsUpdate = true
      bridgeLineMat.opacity = Math.min(
        0.32,
        0.08
          + normalizedBridgeEnergy * 0.16
          + pointerEnergy * 0.08
          + (Math.sin(elapsed * (1.1 * speedFactor)) + 1) * 0.015,
      )

      // Rebuild lines
      let segIdx = 0
      for (let a = 0; a < nodeCount && segIdx < maxSegments; a++) {
        for (let b = a + 1; b < nodeCount && segIdx < maxSegments; b++) {
          const d = nodes[a].position.distanceTo(nodes[b].position)
          if (d < connectionDistance) {
            const base = segIdx * 6
            lineArr[base]     = nodes[a].position.x
            lineArr[base + 1] = nodes[a].position.y
            lineArr[base + 2] = nodes[a].position.z
            lineArr[base + 3] = nodes[b].position.x
            lineArr[base + 4] = nodes[b].position.y
            lineArr[base + 5] = nodes[b].position.z
            segIdx++
          }
        }
      }
      lineGeo.setDrawRange(0, segIdx * 2)
      lineGeo.attributes.position.needsUpdate = true
      lineMat.opacity = Math.min(0.28, 0.11 + (segIdx / maxSegments) * 0.15 + pointerEnergy * 0.08)
      pointsMat.opacity = 0.72 + Math.sin(elapsed * (0.9 * speedFactor)) * 0.08 + pointerEnergy * 0.1
      pointsMat.size = 3.4 + pointerEnergy * (0.8 * motionFactor)

      // Update glow sprites
      for (const glow of glowSprites) {
        glow.sprite.position.copy(nodes[glow.nodeIdx].position)
        const flicker = Math.sin(elapsed * (2 * speedFactor) + glow.nodeIdx) * 0.1 + 0.9
        glow.sprite.scale.setScalar(16 * flicker + pointerEnergy * (7 * motionFactor))
      }

      ringGroup.position.x += (mouseWorld.x * 0.14 - ringGroup.position.x) * 0.08
      ringGroup.position.y += (mouseWorld.y * 0.1 - ringGroup.position.y) * 0.08
      for (let i = 0; i < ringMeshes.length; i++) {
        const ring = ringMeshes[i]
        const material = ring.material as THREE.MeshBasicMaterial
        const pulse = Math.sin(elapsed * (1.2 + i * 0.17) * speedFactor + i * 1.4) * 0.5 + 0.5
        const ringEnergy = 0.35 + pointerEnergy * (0.95 * motionFactor)
        material.opacity = (0.015 + pulse * 0.06) * ringEnergy
        const scale = 1 + pulse * 0.06 + pointerEnergy * (0.08 * motionFactor)
        ring.scale.set(scale, scale, 1)
        ring.rotation.z += (0.0012 + i * 0.00035) * speedFactor
      }

      renderer.render(scene, camera)
    }

    animate()

    // ── Cleanup ───────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(animId)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerdown', onPointerMove)
      container.removeEventListener('pointerleave', onPointerLeave)
      window.removeEventListener('resize', onResize)
      pointsGeo.dispose()
      pointsMat.dispose()
      lineGeo.dispose()
      lineMat.dispose()
      bridgeLineGeo.dispose()
      bridgeLineMat.dispose()
      graphLineGeo.dispose()
      graphLineMat.dispose()
      graphPointsGeo.dispose()
      graphPointsMat.dispose()
      graphFrameGeo.dispose()
      graphFrameMat.dispose()
      glowMat.dispose()
      starsGeo.dispose()
      starsMat.dispose()
      ringMeshes.forEach((ring) => {
        ring.geometry.dispose()
        ;(ring.material as THREE.MeshBasicMaterial).dispose()
      })
      renderer.dispose()
      glowTexture.dispose()
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement)
      }
    }
  }, [motionMultiplier, speedMultiplier])

  return (
    <div
      ref={containerRef}
      className={cn('absolute inset-0 w-full h-full pointer-events-auto', className)}
      aria-hidden="true"
    />
  )
}
